import { peekBranding } from "@/lib/branding";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { AtSign, Bell, Check, MessageCircle, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import PunchMapDialog from "@/components/dashboard/PunchMapDialog";
import PunchRequestDialog from "@/components/dashboard/PunchRequestDialog";
import TimeOffRequestDialog from "@/components/dashboard/TimeOffRequestDialog";
import OvertimeRequestDialog from "@/components/dashboard/OvertimeRequestDialog";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

// Staff sometimes write broadcasts like memos ("TO: All Staff / FROM: … /
// DATE: … / SUBJECT: …"). Strip those header lines from display and surface
// the SUBJECT on its own. Shared with the Broadcast delivery log.
export const parseMemo = (text) => {
  const lines = String(text || "").split("\n");
  let subject = null;
  const kept = [];
  for (const ln of lines) {
    const t = ln.trim();
    const low = t.toLowerCase();
    if (low.startsWith("to:") || low.startsWith("from:") || low.startsWith("date:")) continue;
    if (low.startsWith("subject:")) {
      const s = t.slice(t.indexOf(":") + 1).trim();
      if (s) subject = s;
      continue;
    }
    kept.push(ln);
  }
  return { subject, body: kept.join("\n").replace(/^\n+/, "").replace(/\n+$/, "") };
};

const urlB64ToUint8Array = (b64) => {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

// Render URLs inside message bodies (e.g. the geo-warning map link) as
// tappable links.
const linkify = (text) =>
  String(text).split(/(https?:\/\/[^\s]+)/g).map((p, i) =>
    /^https?:\/\//.test(p) ? (
      <a key={i} href={p} target="_blank" rel="noreferrer" className="break-all font-semibold text-[#2c6eeb] underline underline-offset-2">
        {p}
      </a>
    ) : (
      p
    ),
  );

// Subscribe this device for real Web Push (reaches the phone even with the
// app fully closed). Idempotent — subscribe() returns the existing sub.
const ensurePushSubscribed = async () => {
  try {
    if (!("serviceWorker" in navigator)) return;
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg || !reg.pushManager) return;
    const { data } = await api.get("/notifications/push-key");
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(data.public_key),
    });
    const j = sub.toJSON();
    await api.post("/notifications/push-subscribe", { endpoint: j.endpoint, keys: j.keys });
  } catch { /* push unsupported or denied — badge/dropdown flow still works */ }
};

// Loud 3-note chime for incoming alerts (WebAudio — no asset needed).
// Browsers gate audio behind a user gesture; we try and fail silently.
const playAlertSound = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    [880, 660, 1174].forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = f;
      const t0 = ctx.currentTime + i * 0.14;
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.4, t0 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.5);
      o.connect(g);
      g.connect(ctx.destination);
      o.start(t0);
      o.stop(t0 + 0.55);
    });
  } catch { /* audio blocked until first tap */ }
};

const buzzAlert = () => {
  try { navigator.vibrate?.([300, 120, 300, 120, 500]); } catch { /* unsupported */ }
};

// Distinct Messenger-style "pop" for chat messages — two quick soft notes,
// clearly different from the 3-note alert chime.
const playChatSound = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    [1318, 988].forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "triangle";
      o.frequency.value = f;
      const t0 = ctx.currentTime + i * 0.09;
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.3, t0 + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.22);
      o.connect(g);
      g.connect(ctx.destination);
      o.start(t0);
      o.stop(t0 + 0.25);
    });
  } catch { /* audio blocked until first tap */ }
};

const buzzChat = () => {
  try { navigator.vibrate?.([120, 60, 120]); } catch { /* unsupported */ }
};

// Per-device chat-sound mute (localStorage — silences this browser/phone
// only, not the account). Visual alerts (badge, popup, push) still fire.
const CHAT_MUTED_KEY = "em_chat_sounds_muted";
const chatSoundsMuted = () => {
  try { return localStorage.getItem(CHAT_MUTED_KEY) === "1"; } catch { return false; }
};

// Ask the app (Dashboard) to open the chat dock on a conversation.
const openChatInApp = (conversationId) => {
  try {
    window.dispatchEvent(new CustomEvent("elite:open-chat", { detail: { conversation_id: conversationId } }));
  } catch { /* non-browser env */ }
};

// Ids we've already popped for (session-scoped so a re-poll never re-pops).
const alertedKey = "em_alerted_ids";
const alreadyAlerted = (id) => {
  try {
    const ids = JSON.parse(sessionStorage.getItem(alertedKey) || "[]");
    return ids.includes(id);
  } catch { return false; }
};
const markAlerted = (id) => {
  try {
    const ids = JSON.parse(sessionStorage.getItem(alertedKey) || "[]");
    ids.push(id);
    sessionStorage.setItem(alertedKey, JSON.stringify(ids.slice(-100)));
  } catch { /* private mode */ }
};

/**
 * Notification bell with red unread badge + dropdown of messages. Polls the
 * unread count every 20s; when the count rises, toasts and fires a native
 * notification (phone/desktop alert) if permission was granted. Clicking a
 * row opens the full message and marks it read.
 *
 * Used on the home stats card (and the welcome block) so EVERY signed-in
 * user — including employees — has it.
 */
const NotificationBell = ({ testid = "notif-bell" }) => {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null); // null = not loaded yet
  const [total, setTotal] = useState(0); // all notifications (for "Show more")
  const [loadingMore, setLoadingMore] = useState(false);
  const [active, setActive] = useState(null); // full-message modal
  const [mapTarget, setMapTarget] = useState(null); // punch/geo day-map modal
  const [reqTarget, setReqTarget] = useState(null); // employer punch-request modal
  const [timeOffTarget, setTimeOffTarget] = useState(null); // time-off approval modal
  const [otTarget, setOtTarget] = useState(null); // 8-hour-cap overtime approval modal
  const [pos, setPos] = useState({ left: 8, top: 0, width: 320 });
  const [alert, setAlert] = useState(null); // sticky aggressive popup for a fresh notification
  const [chatMuted, setChatMuted] = useState(chatSoundsMuted); // this device only
  const btnRef = useRef(null);
  const firstPoll = useRef(true);

  const toggleChatSounds = () => {
    const next = !chatMuted;
    try { localStorage.setItem(CHAT_MUTED_KEY, next ? "1" : "0"); } catch { /* private mode */ }
    setChatMuted(next);
    toast.success(next ? "Chat sounds muted on this device" : "Chat sounds on for this device");
  };

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      const n = data.count || 0;
      setUnread((prev) => {
        const latest = data.latest;
        const freshId = latest?.notification_id;
        if (!firstPoll.current && n > prev && freshId && !alreadyAlerted(freshId)) {
          markAlerted(freshId);
          // Aggressive: chime + hard buzz + sticky on-screen popup (plus the
          // toast and a native OS notification as before). Chat messages get
          // their own softer pop + buzz so they sound different.
          const isMention = latest.kind === "chat_mention";
          const isChat = latest.kind === "chat" || isMention;
          if (isMention) {
            // @mentions always sound — they punch through the chat mute.
            playAlertSound();
            buzzAlert();
          } else if (isChat) {
            // Muted on this device? Stay silent but keep the visual alerts.
            if (!chatSoundsMuted()) { playChatSound(); buzzChat(); }
          } else { playAlertSound(); buzzAlert(); }
          // Chat messages: sound + bell badge only — they are listed under
          // Home → Notifications. No sticky popup / toast (too intrusive).
          if (!isChat) {
            setAlert(latest);
            toast.info(latest.title || "New message", { description: "Tap the bell to read it", duration: 6000 });
          }
          try {
            if (typeof Notification !== "undefined" && Notification.permission === "granted") {
              const native = new Notification(latest.title || "Elite Mechanical", { body: latest.body || "" });
              if (isChat && latest.meta?.conversation_id) {
                native.onclick = () => {
                  try { window.focus(); } catch { /* ignore */ }
                  openChatInApp(latest.meta.conversation_id);
                };
              }
            }
          } catch { /* notifications unsupported */ }
        } else if (!firstPoll.current && n > prev && latest?.kind !== "chat" && latest?.kind !== "chat_mention") {
          toast.info(latest?.title || "New message", { description: "Tap the bell to read it", duration: 6000 });
        }
        return n;
      });
    } catch { /* offline — badge stays as-is */ }
    firstPoll.current = false;
  }, []);

  useEffect(() => {
    poll();
    const iv = setInterval(poll, 20000);
    // Already-granted devices (re)register for Web Push on mount.
    ensurePushSubscribed();
    return () => clearInterval(iv);
  }, [poll]);

  const openPanel = async () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const w = Math.min(320, window.innerWidth - 16);
      const left = Math.max(8, Math.min(r.right - w, window.innerWidth - w - 8));
      const estH = 380;
      const below = r.bottom + 6;
      const top = below + estH > window.innerHeight && r.top - estH - 6 > 8 ? r.top - estH - 6 : below;
      setPos({ left, top, width: w });
      // Ask for native-notification permission on first interaction so new
      // messages can alert the phone, then subscribe this device for push.
      try {
        if (typeof Notification !== "undefined" && Notification.permission === "default") {
          await Notification.requestPermission();
        }
        await ensurePushSubscribed();
      } catch { /* unsupported */ }
    }
    if (!open) {
      try {
        const { data } = await api.get("/notifications");
        setItems(data.notifications || []);
        setUnread(data.unread || 0);
        setTotal(data.total ?? (data.notifications || []).length);
      } catch { setItems([]); }
    }
    setOpen((v) => !v);
  };

  // "Show more" at the bottom of the dropdown — pages older notifications
  // in 50-at-a-time (same order: newest first) and appends them.
  const showMore = async () => {
    if (loadingMore || !items) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get(`/notifications?skip=${items.length}&limit=50`);
      const more = data.notifications || [];
      setItems((prev) => [...(prev || []), ...more]);
      if (data.total !== undefined) setTotal(data.total);
    } catch { toast.error("Could not load more notifications"); }
    finally { setLoadingMore(false); }
  };

  // The sticky popup's "View" — load the full notification and route it
  // exactly like tapping its row in the dropdown.
  const viewAlert = async () => {
    const a = alert;
    setAlert(null);
    if (!a?.notification_id) return;
    try {
      const { data } = await api.get("/notifications");
      const list = data.notifications || [];
      setItems(list);
      const full = list.find((x) => x.notification_id === a.notification_id);
      if (full) await openMessage(full);
    } catch { /* offline */ }
  };

  const openMessage = async (n) => {
    // Chat notifications open the actual chat dock on that conversation —
    // never the plain message modal. Location flags deep-link straight to
    // the red cell on the Hours grid; punch requests open the employer
    // confirm/correct dialog; punch & geo-warning notifications open the
    // day map with every logged location; everything else opens the plain
    // message modal.
    if ((n.kind === "chat" || n.kind === "chat_mention" || n.kind?.endsWith("_chat")) && n.meta?.conversation_id) {
      openChatInApp(n.meta.conversation_id);
      setOpen(false);
    } else if (n.kind === "paystub_discrepancy" && n.meta?.disc_id) {
      navigate(`/dashboard?tab=payroll&discrepancy=${encodeURIComponent(n.meta.disc_id)}`);
      setOpen(false);
    } else if (n.kind === "master_scan" && n.meta?.vendor) {
      navigate(`/dashboard?tab=search&sub=${encodeURIComponent(n.meta.vendor)}`);
      setOpen(false);
    } else if (n.meta?.location_flag) {
      const w = n.meta.week_start || "";
      const emp = n.meta.employee_id || "";
      const day = n.meta.day_idx ?? 0;
      navigate(`/dashboard?tab=hours&week=${encodeURIComponent(w)}&emp=${encodeURIComponent(emp)}&day=${day}`);
      setOpen(false);
    } else if (n.meta?.punch_request && n.meta?.entry_id) {
      setReqTarget(n);
      setOpen(false);
    } else if (n.meta?.overtime_request && n.meta?.request_id) {
      setOtTarget(n);
      setOpen(false);
    } else if (n.meta?.time_off_request && n.meta?.request_id) {
      setTimeOffTarget(n);
      setOpen(false);
    } else if (n.meta?.employee_id && n.meta?.day) {
      setMapTarget(n);
      setOpen(false);
    } else {
      setActive(n);
    }
    if (!n.read_at) {
      try { await api.post(`/notifications/${n.notification_id}/read`); } catch { /* ignore */ }
      setItems((prev) => (prev || []).map((x) => (x.notification_id === n.notification_id ? { ...x, read_at: "now" } : x)));
      setUnread((u) => Math.max(0, u - 1));
    }
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); } catch { /* ignore */ }
    setItems((prev) => (prev || []).map((x) => ({ ...x, read_at: x.read_at || "now" })));
    setUnread(0);
  };

  // Per-row X: delete a single notification from your own list.
  const deleteOne = async (n) => {
    try { await api.delete(`/notifications/${n.notification_id}`); } catch { /* ignore */ }
    setItems((prev) => (prev || []).filter((x) => x.notification_id !== n.notification_id));
    setTotal((t) => Math.max(0, t - 1));
    if (!n.read_at) setUnread((u) => Math.max(0, u - 1));
    if (active?.notification_id === n.notification_id) setActive(null);
  };

  return (
    <div className="relative shrink-0" data-testid={testid}>
      <button
        ref={btnRef}
        type="button"
        onClick={openPanel}
        aria-label="Notifications"
        data-testid={`${testid}-button`}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-md border border-white/15 bg-white/5 text-white/80 hover:border-white/30 hover:text-white"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span
            className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 font-mono text-[10px] font-bold text-white"
            data-testid={`${testid}-badge`}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[67]" onClick={() => setOpen(false)} />
          <div
            className="fixed z-[68] overflow-hidden rounded-lg border border-white/15 bg-[#0b0b0f] shadow-xl"
            style={{ left: pos.left, top: pos.top, width: pos.width }}
            data-testid={`${testid}-panel`}
          >
            <div className="flex items-center justify-between border-b border-white/10 px-3 py-2">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Notifications</p>
              <div className="flex items-center gap-1">
                {unread > 0 && (
                  <button
                    type="button"
                    onClick={markAll}
                    data-testid={`${testid}-mark-all`}
                    className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-[#8fa4f0] hover:text-white"
                  >
                    <Check className="h-3 w-3" /> Mark all read
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close notifications"
                  title="Close"
                  data-testid={`${testid}-close-panel`}
                  className="rounded p-1 text-white/50 hover:bg-white/10 hover:text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            {typeof Notification !== "undefined" && Notification.permission === "default" && (
              <button
                type="button"
                onClick={async () => {
                  try {
                    await Notification.requestPermission();
                    await ensurePushSubscribed();
                    toast.success("Alerts enabled for this device");
                  } catch { /* denied */ }
                }}
                data-testid={`${testid}-enable-alerts`}
                className="block w-full border-b border-[#1a3fd1]/30 bg-[#1a3fd1]/10 px-3 py-2 text-center font-mono text-[10px] uppercase tracking-widest text-[#8fa4f0] hover:bg-[#1a3fd1]/20"
              >
                Enable sound &amp; phone alerts for this device
              </button>
            )}
            {/* per-device chat sound mute — silences the pop/buzz only,
                badges and popups keep working */}
            <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2">
              <MessageCircle className="h-3.5 w-3.5 shrink-0 text-[#1a8cff]" />
              <span className="min-w-0 flex-1 text-[11px] text-white/60">
                Chat sounds <span className="text-white/30">(this device)</span>
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={!chatMuted}
                onClick={toggleChatSounds}
                data-testid={`${testid}-chat-sound-toggle`}
                className={`relative h-[18px] w-8 shrink-0 rounded-full transition-colors ${chatMuted ? "bg-white/15" : "bg-[#1a3fd1]"}`}
              >
                <span
                  className={`absolute top-[2px] h-[14px] w-[14px] rounded-full bg-white transition-all ${chatMuted ? "left-[2px]" : "left-[16px]"}`}
                />
              </button>
            </div>
            <ul className="max-h-72 overflow-y-auto" data-testid={`${testid}-list`}>
              {items === null && <li className="px-3 py-6 text-center text-xs text-white/40">Loading…</li>}
              {items !== null && items.length === 0 && (
                <li className="px-3 py-6 text-center text-xs text-white/40" data-testid={`${testid}-empty`}>No notifications yet</li>
              )}
              {(items || []).map((n) => {
                const memo = parseMemo(n.body);
                return (
                <li key={n.notification_id} className="group/notif flex items-start">
                  <button
                    type="button"
                    onClick={() => openMessage(n)}
                    data-testid={`${testid}-item-${n.notification_id}`}
                    className={`flex min-w-0 flex-1 items-start gap-2 px-3 py-2.5 text-left hover:bg-white/5 ${n.read_at ? "opacity-60" : ""}`}
                  >
                    {n.kind === "chat" || n.kind === "chat_mention" ? (
                      n.kind === "chat_mention" ? (
                        <AtSign className={`mt-0.5 h-4 w-4 shrink-0 ${n.read_at ? "text-white/30" : "text-amber-400"}`} data-testid={`${testid}-mention-icon-${n.notification_id}`} />
                      ) : (
                        <MessageCircle className={`mt-0.5 h-4 w-4 shrink-0 ${n.read_at ? "text-white/30" : "text-[#1a8cff]"}`} data-testid={`${testid}-chat-icon-${n.notification_id}`} />
                      )
                    ) : (
                      <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${n.read_at ? "bg-transparent" : n.kind === "geo_warning" ? "bg-red-500" : "bg-[#1a3fd1]"}`} />
                    )}
                    <span className="min-w-0 flex-1">
                      <span className={`block truncate text-sm ${n.read_at ? "text-white/70" : "font-semibold text-white"}`}>{n.title}</span>
                      {/* memo-style messages show ONLY their subject here — never the TO:/FROM:/DATE: lines */}
                      <span className="block truncate text-xs text-white/50">{memo.subject || memo.body}</span>
                      <span className="mt-0.5 block font-mono text-[10px] text-white/40">{n.sender_name ? `${n.sender_name} · ` : ""}{timeAgo(n.created_at)}</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteOne(n)}
                    data-testid={`${testid}-delete-${n.notification_id}`}
                    aria-label="Delete notification"
                    title="Delete"
                    className="mr-1.5 mt-2 shrink-0 rounded p-1 text-white/40 hover:bg-red-500/10 hover:text-red-400 sm:opacity-0 sm:group-hover/notif:opacity-100"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
                );
              })}
            </ul>
            {items !== null && items.length < total && (
              <button
                type="button"
                onClick={showMore}
                disabled={loadingMore}
                data-testid={`${testid}-show-more`}
                className="block w-full border-t border-white/10 px-3 py-2 text-center font-mono text-[10px] uppercase tracking-widest text-[#8fa4f0] hover:bg-white/5 hover:text-white disabled:opacity-60"
              >
                {loadingMore ? "Loading…" : `Show more (${total - items.length} older)`}
              </button>
            )}
          </div>
        </>,
        document.body,
      )}

      {alert && createPortal(
        <div className="pointer-events-none fixed inset-0 z-[95] flex items-start justify-center p-3 pt-16" data-testid="notif-alert-popup">
          <div className="pointer-events-auto w-full max-w-md overflow-hidden rounded-lg border border-[#1a3fd1]/50 bg-[#0b0b0f] text-white shadow-[0_0_60px_rgba(26,63,209,0.45)]">
            <div className="flex items-center gap-3 border-b border-[#1a3fd1]/30 bg-[#1a3fd1]/15 px-4 py-3">
              {alert.kind === "chat_mention" ? (
                <AtSign className="h-4 w-4 text-amber-400" data-testid="notif-alert-mention-icon" />
              ) : alert.kind === "chat" ? (
                <MessageCircle className="h-4 w-4 text-[#1a8cff]" data-testid="notif-alert-chat-icon" />
              ) : (
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#4f6fe8] opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#4f6fe8]" />
                </span>
              )}
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8fa4f0]">
                {alert.kind === "chat_mention" ? "You were mentioned" : alert.kind === "chat" ? "New chat message" : "New alert"}
              </p>
            </div>
            <div className="px-4 py-4">
              <p className="text-base font-bold text-white" data-testid="notif-alert-title">{alert.title}</p>
              {alert.body && <p className="mt-1 whitespace-pre-wrap text-sm text-white/80" data-testid="notif-alert-body">{alert.body}</p>}
              {alert.sender_name && <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-white/40">From: {alert.sender_name}</p>}
            </div>
            <div className="flex justify-end gap-2 border-t border-white/10 px-4 py-3">
              <button
                type="button"
                onClick={() => setAlert(null)}
                data-testid="notif-alert-dismiss"
                className="rounded-md border border-white/15 bg-white/5 px-4 py-2 text-xs text-white/70"
              >
                Dismiss
              </button>
              <button
                type="button"
                onClick={viewAlert}
                data-testid="notif-alert-view"
                className="rounded-md bg-[#1a3fd1] px-4 py-2 text-xs font-semibold text-white hover:bg-[#1633a3]"
              >
                {alert.kind === "chat" || alert.kind === "chat_mention" ? "Open chat" : "View"}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {active && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={() => setActive(null)} data-testid={`${testid}-message-modal`}>
          <div
            className="w-full max-w-md overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0f] text-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* header — logo + sender */}
            <div className="flex items-center gap-3 border-b border-white/10 px-5 py-3.5">
              <img src="/api/branding/icon/elite-logo-dark.png" alt="" className="logo-dark-only h-8 w-auto object-contain" />
              <img src="/api/branding/icon/elite-logo-light.png" alt="" className="logo-light-only h-8 w-auto object-contain" />
              <div className="min-w-0 flex-1">
                <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#8fa4f0]">{peekBranding()?.app_name || "Elite Mechanical"}</p>
                <p className="truncate text-sm font-semibold text-white" data-testid={`${testid}-message-from`}>
                  From {active.sender_name || "System"}
                </p>
              </div>
              <button type="button" onClick={() => setActive(null)} aria-label="Close" data-testid={`${testid}-message-close`} className="rounded p-1.5 text-white/50 hover:bg-white/10 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
            {/* body — broadcast titles duplicate the sender, so skip them;
                memo-style bodies show SUBJECT as the heading, never the
                TO:/FROM:/DATE: header lines */}
            {(() => {
              const memo = parseMemo(active.body);
              const heading = memo.subject || (active.kind !== "broadcast" ? active.title : null);
              return (
                <div className="px-5 py-4">
                  {heading && <h3 className="text-base font-semibold text-white">{heading}</h3>}
                  {memo.body && (
                    <p className={`whitespace-pre-wrap text-sm leading-relaxed text-white/80 ${heading ? "mt-2" : ""}`} data-testid={`${testid}-message-body`}>
                      {linkify(memo.body)}
                    </p>
                  )}
                  <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-white/40">{timeAgo(active.created_at)}</p>
                </div>
              );
            })()}
          </div>
        </div>,
        document.body,
      )}
      {mapTarget && (
        <PunchMapDialog
          employeeId={mapTarget.meta.employee_id}
          day={mapTarget.meta.day}
          title={mapTarget.title}
          sender={mapTarget.sender_name}
          onClose={() => setMapTarget(null)}
          testid={`${testid}-map`}
        />
      )}
      {reqTarget && (
        <PunchRequestDialog
          notification={reqTarget}
          onClose={() => setReqTarget(null)}
          testid={`${testid}-request`}
        />
      )}
      {timeOffTarget && (
        <TimeOffRequestDialog
          notification={timeOffTarget}
          onClose={() => setTimeOffTarget(null)}
          testid={`${testid}-timeoff`}
        />
      )}
      {otTarget && (
        <OvertimeRequestDialog
          notification={otTarget}
          onClose={() => setOtTarget(null)}
          testid={`${testid}-overtime`}
        />
      )}
    </div>
  );
};

export default NotificationBell;
